import React from 'react';
import { Icon } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../../styles/glassmorphism';
import { QualityMetrics } from '../types/dashboard.types';

interface QualityIndicatorProps {
  metrics: QualityMetrics;
}

const QualityIndicator: React.FC<QualityIndicatorProps> = ({ metrics }) => {
  // Calcular média ponderada de qualidade
  const overallQuality = (
    (metrics.greenNumbersPercentage * 1) +
    (metrics.yellowNumbersPercentage * 0.5) +
    (metrics.redNumbersPercentage * 0)
  ) / 100;

  // Determinar status
  const getStatus = (quality: number) => {
    if (quality >= 0.8) return { label: 'Excelente', color: '#27ae60', icon: 'check circle' };
    if (quality >= 0.6) return { label: 'Bom', color: '#f39c12', icon: 'warning' };
    return { label: 'Crítico', color: '#e74c3c', icon: 'times circle' };
  };

  const status = getStatus(overallQuality);

  return (
    <div
      style={{
        ...GlassmorphismTheme.components.glassCard,
        background: 'linear-gradient(135deg, rgba(26, 188, 156, 0.1), rgba(41, 128, 185, 0.1))',
        border: `1px solid ${status.color}33`,
        padding: GlassmorphismTheme.spacing.sm,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <h4 style={{
        margin: `0 0 ${GlassmorphismTheme.spacing.sm} 0`,
        color: '#2c3e50',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '1rem'
      }}>
        <Icon name="signal" style={{ color: status.color }} />
        Qualidade da Conta
      </h4>

      {/* GAUGE CIRCLE */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: GlassmorphismTheme.spacing.sm
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: `conic-gradient(
            ${status.color} 0deg ${(overallQuality * 360)}deg,
            rgba(255, 255, 255, 0.1) ${(overallQuality * 360)}deg 360deg
          )`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 40px ${status.color}30`
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: GlassmorphismTheme.colors.gradients.primary,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{
              fontSize: '1.2rem',
              fontWeight: 700,
              color: status.color
            }}>
              {Math.round(overallQuality * 100)}%
            </span>
            <small style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>Score</small>
          </div>
        </div>
      </div>

      {/* STATUS */}
      <div style={{
        textAlign: 'center',
        marginBottom: GlassmorphismTheme.spacing.sm,
        padding: `${GlassmorphismTheme.spacing.sm} ${GlassmorphismTheme.spacing.md}`,
        background: `${status.color}15`,
        borderRadius: GlassmorphismTheme.borderRadius.md,
        border: `1px solid ${status.color}30`
      }}>
        <Icon
          name={status.icon as any}
          size="large"
          style={{ color: status.color, marginRight: '8px' }}
        />
        <p style={{
          margin: '8px 0 0 0',
          color: status.color,
          fontWeight: 600,
          fontSize: '1rem'
        }}>
          {status.label}
        </p>
      </div>

      {/* BREAKDOWN */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: GlassmorphismTheme.spacing.sm
      }}>
        {/* GREEN */}
        <div style={{
          padding: GlassmorphismTheme.spacing.sm,
          background: 'rgba(39, 174, 96, 0.1)',
          borderRadius: GlassmorphismTheme.borderRadius.sm,
          border: '1px solid rgba(39, 174, 96, 0.3)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#27ae60'
          }}>
            {Math.round(metrics.greenNumbersPercentage)}%
          </div>
          <small style={{ color: '#7f8c8d' }}>Verde</small>
        </div>

        {/* YELLOW */}
        <div style={{
          padding: GlassmorphismTheme.spacing.sm,
          background: 'rgba(243, 156, 18, 0.1)',
          borderRadius: GlassmorphismTheme.borderRadius.sm,
          border: '1px solid rgba(243, 156, 18, 0.3)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#f39c12'
          }}>
            {Math.round(metrics.yellowNumbersPercentage)}%
          </div>
          <small style={{ color: '#7f8c8d' }}>Amarelo</small>
        </div>

        {/* RED */}
        <div style={{
          padding: GlassmorphismTheme.spacing.sm,
          background: 'rgba(231, 76, 60, 0.1)',
          borderRadius: GlassmorphismTheme.borderRadius.sm,
          border: '1px solid rgba(231, 76, 60, 0.3)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#e74c3c'
          }}>
            {Math.round(metrics.redNumbersPercentage)}%
          </div>
          <small style={{ color: '#7f8c8d' }}>Vermelho</small>
        </div>
      </div>

      {/* STATS */}
      <div style={{
        marginTop: GlassmorphismTheme.spacing.md,
        paddingTop: GlassmorphismTheme.spacing.md,
        borderTop: GlassmorphismTheme.borders.glassLight,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: GlassmorphismTheme.spacing.sm
      }}>
        <div>
          <small style={{ color: '#95a5a6' }}>Taxa Falha</small>
          <p style={{
            margin: '4px 0 0 0',
            fontWeight: 600,
            color: '#2c3e50',
            fontSize: '1.1rem'
          }}>
            {metrics.failureRate.toFixed(2)}%
          </p>
        </div>
        <div>
          <small style={{ color: '#95a5a6' }}>Tentativas Médias</small>
          <p style={{
            margin: '4px 0 0 0',
            fontWeight: 600,
            color: '#2c3e50',
            fontSize: '1.1rem'
          }}>
            {metrics.averageSendAttempts.toFixed(1)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QualityIndicator;
